import React, { createContext, useContext, ReactNode, useReducer } from 'react';
import { Appointment, GetAppointmentsQueryParams } from '../types/appointment.types';
import { useAppointments } from '../hooks/useAppointments';

// Types for the data context
type AppointmentsDataContextType = ReturnType<typeof useAppointments>;

// Types for the UI state
interface AppointmentsUIState {
  selectedAppointment: Appointment | null;
  isDialogOpen: boolean;
  dialogType: 'create' | 'edit' | 'view' | 'delete';
  loading: boolean;
}

type AppointmentsUIAction =
  | { type: 'SET_SELECTED_APPOINTMENT'; payload: Appointment | null }
  | { type: 'OPEN_DIALOG'; payload: 'create' | 'edit' | 'view' | 'delete' }
  | { type: 'CLOSE_DIALOG' }
  | { type: 'SET_LOADING'; payload: boolean };

// UI Reducer
const uiReducer = (state: AppointmentsUIState, action: AppointmentsUIAction): AppointmentsUIState => {
  switch (action.type) {
    case 'SET_SELECTED_APPOINTMENT':
      return { ...state, selectedAppointment: action.payload };
    case 'OPEN_DIALOG':
      return { ...state, isDialogOpen: true, dialogType: action.payload };
    case 'CLOSE_DIALOG':
      return { ...state, isDialogOpen: false };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    default:
      return state;
  }
};

// Contexts
const AppointmentsDataContext = createContext<AppointmentsDataContextType | undefined>(undefined);
const AppointmentsUIContext = createContext<{
  state: AppointmentsUIState;
  openDialog: (type: 'create' | 'edit' | 'view' | 'delete', appointment?: Appointment | null) => void;
  closeDialog: () => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
} | undefined>(undefined);

// Provider component
type AppointmentsProviderProps = {
  children: ReactNode;
  initialParams?: GetAppointmentsQueryParams;
};

export const AppointmentsProvider: React.FC<AppointmentsProviderProps> = ({
  children,
  initialParams = {},
}) => {
  // Data state
  const appointmentsData = useAppointments(initialParams);
  
  // UI state
  const [uiState, uiDispatch] = useReducer(uiReducer, {
    selectedAppointment: null,
    isDialogOpen: false,
    dialogType: 'view',
    loading: false,
  });

  // UI actions
  const openDialog = (type: 'create' | 'edit' | 'view' | 'delete', appointment: Appointment | null = null) => {
    if (appointment) {
      uiDispatch({ type: 'SET_SELECTED_APPOINTMENT', payload: appointment });
    }
    uiDispatch({ type: 'OPEN_DIALOG', payload: type });
  };

  const closeDialog = () => {
    uiDispatch({ type: 'CLOSE_DIALOG' });
    if (uiState.dialogType === 'delete' || uiState.dialogType === 'edit') {
      // Clear selection after closing edit/delete dialogs
      uiDispatch({ type: 'SET_SELECTED_APPOINTMENT', payload: null });
    }
  };
  
  const setLoading = (loading: boolean) => {
    uiDispatch({ type: 'SET_LOADING', payload: loading });
  };
  
  return (
    <AppointmentsDataContext.Provider value={appointmentsData}>
      <AppointmentsUIContext.Provider value={{
        state: uiState,
        openDialog,
        closeDialog,
        loading: uiState.loading,
        setLoading,
      }}>
        {children}
      </AppointmentsUIContext.Provider>
    </AppointmentsDataContext.Provider>
  );
};

// Custom hooks - using const declarations for Fast Refresh compatibility
const useAppointmentsContext = (): AppointmentsDataContextType => {
  const context = useContext(AppointmentsDataContext);
  if (context === undefined) {
    throw new Error('useAppointmentsContext must be used within an AppointmentsProvider');
  }
  return context;
};

const useAppointmentsUI = () => {
  const context = useContext(AppointmentsUIContext);
  if (context === undefined) {
    throw new Error('useAppointmentsUI must be used within an AppointmentsProvider');
  }
  return context;
};

export { useAppointmentsContext, useAppointmentsUI };
